import React, { useState, useMemo, useEffect, useCallback } from 'react'
// @ts-ignore no types
import _ from 'lodash'
import _cx from 'classnames/bind'
import {
  Text,
  Form,
  InputField,
  TextAreaField,
  useApplicationModals,
} from '@adc/ui-adc'
import type { primitives } from '@adc/blueprint'
import { ListingParty } from '@adc/sdk/types'
import { CombinedListingType, useListingContext } from '../../context/listing'
import {
  TRANSIENT_MESSAGE_CONTACT_AGENT_TITLE,
  TRANSIENT_MESSAGE_CONTACT_AGENT_BODY,
} from '../../statics'
import { contactListingAgent } from '../../providers/contact'
import { getApplicationNotificationsContext } from '../../utils/applicationNotificationsBridge'
import { formatPhoneNumber } from '../../utils/formatters'
import {
  required,
  email as emailValidator,
  maxLength,
} from '../../utils/validators'
// @ts-ignore no types
import cls from './index.scss'

const cx = _cx.bind(cls)

interface ErrorState {
  firstName?: string
  lastName?: string
  email?: string
  message?: string
}
interface ModalBodyProps {
  listing: CombinedListingType
  elmId: string
  setErrors: any
  errors: ErrorState
  globalError: boolean
  setGlobalError: any
  setFormValues: any
  formValues: FormValues
}
interface FormChangeEvent {
  name: string
  value: string | number | boolean
  validators: any
}
interface userContactModalProps {
  user: primitives.AuctionUser
}
interface FormValues {
  firstName: string
  lastName: string
  email: string
  message: string
}

export const getContactAgentInfo = (listing: CombinedListingType) => {
  const listingParties = _.keyBy(listing.parties, 'party_role')
  const brokerParty = listingParties?.BROKER as ListingParty
  const listingAgentParty = listing.auction?.is_online
    ? listingParties.LISTING_AGENT
    : listingParties.FORECLOSURE_ATTORNEY
  const listingAgency = brokerParty?.party_name || undefined
  const agentName = listingAgentParty?.party_name || undefined
  const agentPhone = brokerParty?.party_phone || undefined
  const agentEmail = brokerParty?.party_email || undefined
  return { listingAgency, agentName, agentPhone, agentEmail }
}

const ContactModalBody = ({
  listing,
  elmId,
  setErrors,
  errors,
  globalError,
  setGlobalError,
  setFormValues,
  formValues,
}: ModalBodyProps) => {
  const { firstName, lastName, email, message } = formValues
  const { listingAgency, agentName, agentPhone, agentEmail } =
    getContactAgentInfo(listing)

  const maxMessageLength = 500
  const onFormChange = (
    _e: any,
    { name, value, validators }: FormChangeEvent
  ) => {
    let error = null
    // BUG in eslint
    // eslint-disable-next-line no-unused-vars
    for (const validator of validators) {
      error = validator(value)
      if (error) break
      else error = false
    }
    setFormValues({ ...formValues, [name]: value })
    setErrors({ ...errors, [name]: error })
    setGlobalError(false)
  }

  return (
    <div>
      {listingAgency && (
        <Text className='u-mt-1 u-mb-3' data-elm-id={`${elmId}_listing_agency`}>
          Listed by {listingAgency}
        </Text>
      )}
      {agentName && (
        <Text
          weight='heavy'
          className='u-mb-1'
          data-elm-id={`${elmId}_agent_name`}
        >
          {agentName}
        </Text>
      )}
      {agentEmail && (
        <Text className='u-mb-1'>
          <a href={`mailto:${agentEmail}`} data-elm-id={`${elmId}_agent_email`}>
            {agentEmail}
          </a>
        </Text>
      )}
      {agentPhone && (
        <Text className='u-pb-5'>
          <a href={`tel:${agentPhone}`} data-elm-id={`${elmId}_agent_phone`}>
            {formatPhoneNumber(agentPhone)}
          </a>
        </Text>
      )}
      <Text className='u-mt-5 u-mb-3'>Enter your information below:</Text>
      {globalError && (
        <div className={cx('banner', 'role-error', 'u-mb-3')}>
          We're unable to contact the agent right now. Please try again later.
        </div>
      )}
      <Form className='u-mb-4'>
        <Form.Group widths='equal'>
          <InputField
            name='firstName'
            elmId={`${elmId}_first_name`}
            placeholder='First name'
            validators={[required]}
            error={errors.firstName}
            value={firstName}
            onChange={onFormChange}
            disabled
            touched
          />
          <InputField
            name='lastName'
            elmId={`${elmId}_last_name`}
            value={lastName}
            placeholder='Last Name'
            validators={[required]}
            error={errors.lastName}
            onChange={onFormChange}
            disabled
            touched
          />
        </Form.Group>
        <InputField
          name='email'
          elmId={`${elmId}_email`}
          value={email}
          placeholder='Email'
          validators={[required, emailValidator]}
          error={errors.email}
          onChange={onFormChange}
          disabled
          touched
        />
        <TextAreaField
          name='message'
          elmId={`${elmId}_message`}
          value={message}
          placeholder='Message To Agent'
          validators={[required, maxLength(maxMessageLength)]}
          error={errors.message}
          onChange={onFormChange}
          rows={2}
          touched
        />
      </Form>
    </div>
  )
}

export const useContactModal = ({ user }: userContactModalProps) => {
  const { listing } = useListingContext()
  const elmId = 'contact_agent_modal'
  const applicationModals = useApplicationModals()
  const [formValues, setFormValues] = useState({
    firstName: user.logged_in ? user.first_name ?? '' : '',
    lastName: user.logged_in ? user.last_name ?? '' : '',
    email: user.logged_in ? user.email ?? '' : '',
    message: `I am interested in ${String(listing?.formatted_address)}`,
  })
  const [isOpen, setIsOpen] = useState(false)
  const [errors, setErrors] = useState({})
  const [globalError, setGlobalError] = useState(false)
  const hasErrors = Object.values(errors).some(elem => !!elem)
  const submitForm = useCallback(async () => {
    const { message } = formValues
    const notifications = getApplicationNotificationsContext()
    try {
      const res = await contactListingAgent({
        listingId: listing.listing_id,
        message,
      })
      if (!res.ok) {
        throw new Error(res.statusText)
      } else {
        resetForm()
        notifications.overlayBanner.open({
          title: TRANSIENT_MESSAGE_CONTACT_AGENT_TITLE,
          message: TRANSIENT_MESSAGE_CONTACT_AGENT_BODY,
        })
      }
    } catch (e) {
      setGlobalError(true)
    }
  }, [formValues, listing])
  const resetForm = useCallback(() => {
    setIsOpen(false)
    setFormValues({
      firstName: user.logged_in ? user.first_name ?? '' : '',
      lastName: user.logged_in ? user.last_name ?? '' : '',
      email: user.logged_in ? user.email ?? '' : '',
      message: `I am interested in ${String(listing?.formatted_address)}`,
    })
  }, [user, listing])

  const modalProps = useMemo(
    () => ({
      children: (
        <ContactModalBody
          listing={listing}
          elmId={elmId}
          setErrors={setErrors}
          errors={errors}
          globalError={globalError}
          setGlobalError={setGlobalError}
          setFormValues={setFormValues}
          formValues={formValues}
        />
      ),
      elmId,
      size: 'small',
      title: 'Get In Touch',
      confirmButton: {
        label: 'Submit',
        onClick: submitForm,
        disabled: hasErrors,
      },
      cancelButton: {
        label: 'Cancel',
        onClick: resetForm,
      },
      formatConfirmError: () => {
        return (
          globalError && (
            <div className={cx('banner', 'role-error', 'u-mb-3')}>
              We're unable to contact the agent right now. Please try again
              later.
            </div>
          )
        )
      },
    }),
    [elmId, formValues, hasErrors, globalError, submitForm, resetForm]
  )
  useEffect(() => {
    // @ts-ignore modal types need updating, todo...
    if (isOpen) applicationModals.update(modalProps)
  }, [modalProps, isOpen])

  const open = useCallback(() => {
    // @ts-ignore modal types need updating, todo...
    applicationModals.open('confirmation', modalProps)
    setIsOpen(true)
  }, [modalProps, applicationModals])
  useEffect(() => {
    if (user.logged_in && user.user_id) {
      setFormValues({
        firstName: user.first_name ?? '',
        lastName: user.last_name ?? '',
        email: user.email ?? '',
        message: `I am interested in ${String(listing?.formatted_address)}`,
      })
    }
  }, [user, listing?.formatted_address, setFormValues])

  return {
    open,
  }
}
